import React, { useCallback, useMemo, useState } from 'react';
import { LuCheck, LuPencil, LuTrash2, LuX } from 'react-icons/lu';
import { useProducts } from '../ProductContext';
import { useAuth } from '../AuthContext';
import {
  factoryCategoryShort,
  factoryDefaultUnit,
  isFactoryRawMaterialCategory,
  resolveFactoryMaterialUnit,
} from '../constants/factoryStockCategories';
import { PRODUCT_UNITS, productUnitLabel } from '../constants/productUnits';
import { useFactoryRawMaterialCategories } from '../hooks/useFactoryRawMaterialCategories';
import { normalizeRole } from '../utils/userLocale';
import './RawMaterialsOverview.css';

function categoryEditLabel(fullCategory) {
  const short = factoryCategoryShort(fullCategory);
  return short === '—' ? '' : short;
}

export default function RawMaterialsOverview({ onNavigate }) {
  const { user } = useAuth();
  const { products, inventoryLoading, updateProduct, deleteProduct, refreshProducts } = useProducts();
  const {
    categories,
    selectOptions,
    loading: categoriesLoading,
    updateCategory,
    deleteCategory,
  } = useFactoryRawMaterialCategories();
  const role = normalizeRole(user?.role);
  const canModerate = role === 'owner' || role === 'manager';

  const [editMaterialId, setEditMaterialId] = useState(null);
  const [materialForm, setMaterialForm] = useState({ name: '', category: '', unit: 'bag' });
  const [editCategoryFrom, setEditCategoryFrom] = useState(null);
  const [categoryFormName, setCategoryFormName] = useState('');
  const [actionId, setActionId] = useState(null);
  const [categoryError, setCategoryError] = useState('');

  const go = (key) => {
    if (typeof onNavigate === 'function') onNavigate(key);
  };

  const rawMaterials = useMemo(
    () => (products || []).filter((p) => isFactoryRawMaterialCategory(p.category)),
    [products]
  );

  const materialCountByCategory = useMemo(() => {
    const map = new Map();
    for (const m of rawMaterials) {
      const cat = m.category || '';
      map.set(cat, (map.get(cat) || 0) + 1);
    }
    return map;
  }, [rawMaterials]);

  const sortedMaterials = useMemo(
    () =>
      rawMaterials
        .slice()
        .sort((a, b) => {
          const c = String(a.category || '').localeCompare(String(b.category || ''));
          return c !== 0 ? c : String(a.name).localeCompare(String(b.name));
        }),
    [rawMaterials]
  );

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => factoryCategoryShort(a).localeCompare(factoryCategoryShort(b))),
    [categories]
  );

  const startMaterialEdit = useCallback((m) => {
    setEditCategoryFrom(null);
    setCategoryFormName('');
    setEditMaterialId(String(m.id));
    setMaterialForm({
      name: m.name || '',
      category: m.category || '',
      unit: resolveFactoryMaterialUnit(m),
    });
  }, []);

  const cancelMaterialEdit = useCallback(() => {
    setEditMaterialId(null);
  }, []);

  const saveMaterialEdit = useCallback(
    async (m) => {
      const name = materialForm.name.trim();
      if (!name || !materialForm.category) return;
      setActionId(`mat-${m.id}`);
      try {
        await updateProduct(m.id, {
          name,
          category: materialForm.category,
          quantity: Number(m.quantity) || 0,
          purchasingPrice: Number(m.purchasingPrice) || 0,
          sellingPrice: Number(m.sellingPrice) || 0,
          lowStockLimit: Number(m.lowStockLimit) || 0,
          unit: materialForm.unit || m.unit || 'bag',
        });
        setEditMaterialId(null);
      } catch {
        /* notify from context */
      } finally {
        setActionId(null);
      }
    },
    [materialForm, updateProduct]
  );

  const removeMaterial = useCallback(
    async (m) => {
      if (!window.confirm(`Delete "${m.name}" from raw materials?`)) return;
      setActionId(`mat-${m.id}`);
      try {
        await deleteProduct(m.id, m.name);
        if (String(editMaterialId) === String(m.id)) setEditMaterialId(null);
      } catch {
        /* notify from context */
      } finally {
        setActionId(null);
      }
    },
    [deleteProduct, editMaterialId]
  );

  const startCategoryEdit = useCallback((cat) => {
    setEditMaterialId(null);
    setEditCategoryFrom(cat);
    setCategoryFormName(categoryEditLabel(cat));
    setCategoryError('');
  }, []);

  const cancelCategoryEdit = useCallback(() => {
    setEditCategoryFrom(null);
    setCategoryFormName('');
    setCategoryError('');
  }, []);

  const saveCategoryEdit = useCallback(async () => {
    const from = editCategoryFrom;
    const label = categoryFormName.trim();
    if (!from || !label) return;
    setActionId(`cat-${from}`);
    setCategoryError('');
    try {
      await updateCategory(from, label);
      await refreshProducts();
      setEditCategoryFrom(null);
      setCategoryFormName('');
    } catch (err) {
      setCategoryError(err.body?.message || err.message || 'Could not update category.');
    } finally {
      setActionId(null);
    }
  }, [editCategoryFrom, categoryFormName, updateCategory, refreshProducts]);

  const removeCategory = useCallback(
    async (cat) => {
      const count = materialCountByCategory.get(cat) || 0;
      if (count > 0) {
        setCategoryError('Remove or move all materials in this category before deleting it.');
        return;
      }
      if (!window.confirm(`Delete category "${factoryCategoryShort(cat)}"?`)) return;
      setActionId(`cat-${cat}`);
      setCategoryError('');
      try {
        await deleteCategory(cat);
        if (editCategoryFrom === cat) cancelCategoryEdit();
      } catch (err) {
        setCategoryError(err.body?.message || err.message || 'Could not delete category.');
      } finally {
        setActionId(null);
      }
    },
    [materialCountByCategory, deleteCategory, editCategoryFrom, cancelCategoryEdit]
  );

  const loading = inventoryLoading || categoriesLoading;
  const busy = actionId != null;
  const editingSomething = editMaterialId != null || editCategoryFrom != null;

  return (
    <section className="rm-overview page-bleed">
      <header className="rm-overview__hero rm-overview__hero--simple">
        <h1 className="rm-overview__title">Raw materials</h1>
        <div className="rm-overview__actions rm-overview__actions--hero">
          <button type="button" className="rm-overview__action" onClick={() => go('add-raw-material')}>
            Add materials
          </button>
          <button
            type="button"
            className="rm-overview__action--ghost rm-overview__action"
            onClick={() => go('add-raw-material-category')}
          >
            Add category
          </button>
        </div>
      </header>

      {loading ? (
        <p className="rm-overview__empty">Loading…</p>
      ) : (
        <>
          <section className="rm-overview__section" aria-labelledby="rm-categories-title">
            <h2 id="rm-categories-title" className="rm-overview__section-title">
              Categories
            </h2>
            {categoryError ? (
              <p className="rm-overview__error" role="alert">
                {categoryError}
              </p>
            ) : null}
            {categories.length === 0 ? (
              <p className="rm-overview__muted">No categories yet.</p>
            ) : (
              <div className="rm-overview__table-wrap">
                <table className="rm-overview__table">
                  <thead>
                    <tr>
                      <th scope="col">Category</th>
                      <th scope="col">Materials</th>
                      {canModerate ? <th scope="col">Action</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedCategories.map((cat) => {
                      const isEditing = editCategoryFrom === cat;
                      const rowBusy = actionId === `cat-${cat}`;
                      const count = materialCountByCategory.get(cat) || 0;
            return (
                        <tr key={cat} className={isEditing ? 'rm-overview__row--edit' : ''}>
                          <td className="rm-overview__mat-name">
                            {isEditing ? (
                              <input
                                type="text"
                                className="rm-overview__input"
                                value={categoryFormName}
                                onChange={(e) => setCategoryFormName(e.target.value)}
                                disabled={rowBusy}
                                aria-label="Category name"
                                placeholder="e.g. Cement, Water"
                              />
                            ) : (
                              factoryCategoryShort(cat)
                            )}
                          </td>
                          <td className="rm-overview__mat-cat">{count}</td>
                          {canModerate ? (
                            <td className="rm-overview__actions-cell">
                              {isEditing ? (
                                <div className="rm-overview__row-actions">
                                  <button
                                    type="button"
                                    className="rm-overview__icon-btn rm-overview__icon-btn--save"
                                    title="Save"
                                    disabled={rowBusy || !categoryFormName.trim()}
                                    onClick={() => void saveCategoryEdit()}
                                  >
                                    <LuCheck size={16} aria-hidden />
                                  </button>
                                  <button
                                    type="button"
                                    className="rm-overview__icon-btn"
                                    title="Cancel"
                                    disabled={rowBusy}
                                    onClick={cancelCategoryEdit}
                                  >
                                    <LuX size={16} aria-hidden />
                                  </button>
                                </div>
                              ) : (
                                <div className="rm-overview__row-actions">
                                  <button
                                    type="button"
                                    className="rm-overview__icon-btn"
                                    title="Edit"
                                    disabled={busy || editingSomething}
                                    onClick={() => startCategoryEdit(cat)}
                                  >
                                    <LuPencil size={15} aria-hidden />
                                  </button>
                                  <button
                                    type="button"
                                    className="rm-overview__icon-btn rm-overview__icon-btn--danger"
                                    title={count > 0 ? 'Remove materials first' : 'Delete'}
                                    disabled={busy || count > 0}
                                    onClick={() => void removeCategory(cat)}
                                  >
                                    <LuTrash2 size={15} aria-hidden />
                                  </button>
                                </div>
                              )}
                            </td>
                  ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
            )}
          </section>

          <section className="rm-overview__section" aria-labelledby="rm-materials-title">
            <h2 id="rm-materials-title" className="rm-overview__section-title">
              Materials
            </h2>
            {rawMaterials.length === 0 ? (
              <div className="rm-overview__empty">
                <p>No materials yet. Add a category if needed, then add materials.</p>
      </div>
            ) : (
              <div className="rm-overview__table-wrap">
                <table className="rm-overview__table">
                  <thead>
                    <tr>
                      <th scope="col">Material</th>
                      <th scope="col">Category</th>
                      <th scope="col">Unit</th>
                      {canModerate ? <th scope="col">Action</th> : null}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedMaterials.map((m) => {
                      const isEditing = String(editMaterialId) === String(m.id);
                      const rowBusy = actionId === `mat-${m.id}`;
                      return (
                        <tr key={m.id} className={isEditing ? 'rm-overview__row--edit' : ''}>
                          <td className="rm-overview__mat-name">
                            {isEditing ? (
                              <input
                                type="text"
                                className="rm-overview__input"
                                value={materialForm.name}
                                onChange={(e) => setMaterialForm((f) => ({ ...f, name: e.target.value }))}
                                disabled={rowBusy}
                                aria-label="Material name"
                              />
                            ) : (
                              m.name
                            )}
                          </td>
                          <td className="rm-overview__mat-cat">
                            {isEditing ? (
                              <select
                                className="rm-overview__input rm-overview__select"
                                value={materialForm.category}
                                onChange={(e) => {
                                  const next = e.target.value;
                                  setMaterialForm((f) => ({
                                    ...f,
                                    category: next,
                                    unit: factoryDefaultUnit(next),
                                  }));
                                }}
                                disabled={rowBusy}
                                aria-label="Category"
                              >
                                {selectOptions.map((opt) => (
                                  <option key={opt.value || 'empty'} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              factoryCategoryShort(m.category)
                            )}
                          </td>
                          <td className="rm-overview__mat-unit">
                            {isEditing ? (
                              <select
                                className="rm-overview__input rm-overview__select"
                                value={materialForm.unit}
                                onChange={(e) => setMaterialForm((f) => ({ ...f, unit: e.target.value }))}
                                disabled={rowBusy}
                                aria-label="Unit"
                              >
                                {PRODUCT_UNITS.map((u) => (
                                  <option key={u.value} value={u.value}>
                                    {u.label}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              productUnitLabel(resolveFactoryMaterialUnit(m))
                            )}
                          </td>
                          {canModerate ? (
                            <td className="rm-overview__actions-cell">
                              {isEditing ? (
                                <div className="rm-overview__row-actions">
                                  <button
                                    type="button"
                                    className="rm-overview__icon-btn rm-overview__icon-btn--save"
                                    title="Save"
                                    disabled={rowBusy || !materialForm.name.trim() || !materialForm.category}
                                    onClick={() => void saveMaterialEdit(m)}
                                  >
                                    <LuCheck size={16} aria-hidden />
                                  </button>
                                  <button
                                    type="button"
                                    className="rm-overview__icon-btn"
                                    title="Cancel"
                                    disabled={rowBusy}
                                    onClick={cancelMaterialEdit}
                                  >
                                    <LuX size={16} aria-hidden />
                                  </button>
        </div>
                              ) : (
                                <div className="rm-overview__row-actions">
                                  <button
                                    type="button"
                                    className="rm-overview__icon-btn"
                                    title="Edit"
                                    disabled={busy || editingSomething}
                                    onClick={() => startMaterialEdit(m)}
                                  >
                                    <LuPencil size={15} aria-hidden />
                                  </button>
                                  <button
                                    type="button"
                                    className="rm-overview__icon-btn rm-overview__icon-btn--danger"
                                    title="Delete"
                                    disabled={busy}
                                    onClick={() => void removeMaterial(m)}
                                  >
                                    <LuTrash2 size={15} aria-hidden />
                                  </button>
      </div>
                              )}
                            </td>
                          ) : null}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
    </div>
            )}
            {!canModerate ? (
              <p className="rm-overview__muted" style={{ marginTop: '0.75rem' }}>
                Only the owner can edit or delete categories and materials.
              </p>
            ) : null}
          </section>
        </>
      )}
    </section>
  );
}
